import { useNotification } from "../../../context/notification-provider";
import { useDialogs } from "../../dialog/use-dialog";
import {
  createEmailIngestion,
  createSftpIngestion,
  deleteEmailIngestion,
  deleteSftpIngestion,
  updateEmailIngestion,
  updateSftpIngestion,
} from "../services/client-ingestion-service";
import {
  EmailIngestion,
  EmailIngestionFormValues,
  IngestionKind,
  SftpIngestion,
  SftpIngestionFormValues,
} from "../types";
import getApiErrorMessage from "../utils/api-error";
import { toEmailRequest, toSftpRequest } from "../utils/mappers";

const NOTIFICATION_DURATION = 3000;

const KIND_LABELS: Record<IngestionKind, { name: string; inSentence: string }> =
  {
    sftp: { name: "SFTP", inSentence: "SFTP" },
    email: { name: "Email", inSentence: "email" },
  };

interface UseIngestionActionsReturn {
  /** Creates when `existing` is undefined, otherwise updates. Resolves true on success. */
  saveSftpIngestion: (
    clientId: number,
    existing: SftpIngestion | undefined,
    values: SftpIngestionFormValues,
  ) => Promise<boolean>;
  /** Asks for confirmation first. Resolves true only when deleted. */
  deleteSftpIngestion: (ingestion: SftpIngestion) => Promise<boolean>;
  saveEmailIngestion: (
    clientId: number,
    existing: EmailIngestion | undefined,
    values: EmailIngestionFormValues,
  ) => Promise<boolean>;
  deleteEmailIngestion: (ingestion: EmailIngestion) => Promise<boolean>;
}

const useIngestionActions = (): UseIngestionActionsReturn => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const notifySuccess = (message: string) =>
    showNotification({
      message,
      severity: "success",
      duration: NOTIFICATION_DURATION,
    });

  const notifyError = (error: unknown, fallback: string) =>
    showNotification({
      message: getApiErrorMessage(error, fallback),
      severity: "error",
      duration: NOTIFICATION_DURATION,
    });

  const runDelete = async (
    kind: IngestionKind,
    description: string,
    remove: () => Promise<void>,
  ): Promise<boolean> => {
    const { name, inSentence } = KIND_LABELS[kind];
    const confirmed = await confirm(
      `Are you sure you want to delete the ${inSentence} ingestion "${description}"? ` +
        "This cannot be undone. Its server details and setting are also removed when nothing else uses them.",
      {
        title: `Delete ${name} Ingestion`,
        okText: "Delete",
        cancelText: "Cancel",
        severity: "error",
      },
    );
    if (confirmed !== true) {
      return false;
    }

    try {
      await remove();
      notifySuccess(`${name} ingestion "${description}" deleted.`);
      return true;
    } catch (error) {
      notifyError(error, `Failed to delete the ${inSentence} ingestion.`);
      return false;
    }
  };

  const saveSftpIngestion = async (
    clientId: number,
    existing: SftpIngestion | undefined,
    values: SftpIngestionFormValues,
  ): Promise<boolean> => {
    const request = toSftpRequest(values);
    try {
      if (existing) {
        await updateSftpIngestion(existing.id, request);
        notifySuccess(`SFTP ingestion "${request.description}" updated.`);
      } else {
        await createSftpIngestion({ ...request, clientId });
        notifySuccess(`SFTP ingestion "${request.description}" created.`);
      }
      return true;
    } catch (error) {
      notifyError(error, "Failed to save the SFTP ingestion.");
      return false;
    }
  };

  const saveEmailIngestion = async (
    clientId: number,
    existing: EmailIngestion | undefined,
    values: EmailIngestionFormValues,
  ): Promise<boolean> => {
    const request = toEmailRequest(values);
    try {
      if (existing) {
        await updateEmailIngestion(existing.id, request);
        notifySuccess(`Email ingestion "${request.description}" updated.`);
      } else {
        await createEmailIngestion({ ...request, clientId });
        notifySuccess(`Email ingestion "${request.description}" created.`);
      }
      return true;
    } catch (error) {
      notifyError(error, "Failed to save the email ingestion.");
      return false;
    }
  };

  return {
    saveSftpIngestion,
    deleteSftpIngestion: (ingestion) =>
      runDelete("sftp", ingestion.description, () =>
        deleteSftpIngestion(ingestion.id),
      ),
    saveEmailIngestion,
    deleteEmailIngestion: (ingestion) =>
      runDelete("email", ingestion.description, () =>
        deleteEmailIngestion(ingestion.id),
      ),
  };
};

export default useIngestionActions;
